"use client";

// WO-GF-001-P1B-S32 (F186) — THE MOUNTAIN-SWITCH INTENT, in ONE place.
//
// WHY THIS MODULE EXISTS AT ALL. F186 gives the change-mountain badge a second way to switch mountains
// (a small dropdown) alongside the existing full-screen R1a selector. The founder's requirement is that
// dates, passengers and cart context survive the switch through BOTH paths. Two code paths that agree
// today because someone kept them in sync is exactly the drift this estate keeps paying for — the
// hardcoded ONLINE chip and the two divergent mountain selectors were both that shape. So the intent is
// READ once and APPLIED once, here, and both paths call it. They cannot disagree, because there is only
// one of them.
//
// WHAT "INTENT" MEANS, precisely:
//   • MODE      — the entry point's mode (marketplace / transport / directory / neutral). This is the
//                 pre-existing RW3-3 / F89 R1 rule and is unchanged: marketplace → /deals-and-packages,
//                 directory → /profile, transport and neutral → the mountain hub.
//   • SELECTION — the dates and passenger counts the visitor has already chosen. These live in the URL
//                 query on the booking funnel (startDate / endDate / adult / child / senior / student /
//                 noStore). Before S32 they were DROPPED on a mountain switch: destFor() resolved a bare
//                 destination path and the chosen dates were simply gone.
//   • CART      — NOT carried in the URL. The cart is zustand-persisted under `sc-cart` in localStorage,
//                 so it survives a navigation by construction. It is listed here because the gate
//                 asserts it explicitly: "it survives because of how storage works" is a claim worth
//                 proving rather than assuming, and if the cart key ever moves this is where the note is.

import { mountainCodeFromPath, hudNavHref, mountainHubHref } from "./hub-nav";
import type { MountainSelectMode } from "./MountainSelectModal";

/** The query keys that carry a visitor's existing selection. Nothing else is copied forward. */
const CARRIED_PARAMS = ["startDate", "endDate", "adult", "child", "senior", "student", "noStore"] as const;

export type SwitchIntent = {
  mode: MountainSelectMode;
  /** the carried selection, in a stable key order so two intents can be compared as strings */
  params: Record<string, string>;
  /** how many lines are in the cart at read time — asserted across the switch, never rebuilt */
  cartCount: number;
};

/** How many lines the persisted cart holds. Read defensively: a missing/failed store is 0, never a throw. */
function readCartCount(): number {
  try {
    const raw = window.localStorage.getItem("sc-cart");
    if (!raw) return 0;
    const parsed = JSON.parse(raw) as { state?: { items?: unknown[] } };
    return Array.isArray(parsed?.state?.items) ? parsed.state.items.length : 0;
  } catch {
    return 0;
  }
}

/**
 * Read the visitor's current intent from the surface they are standing on.
 *
 * Only keys that are actually PRESENT are captured — an absent `endDate` stays absent rather than
 * becoming an empty string, so a one-way search does not silently acquire an empty return leg.
 */
export function readSwitchIntent(mode: MountainSelectMode = null): SwitchIntent {
  const params: Record<string, string> = {};
  try {
    const q = new URLSearchParams(window.location.search);
    for (const k of CARRIED_PARAMS) {
      const v = q.get(k);
      if (v !== null && v !== "") params[k] = v;
    }
  } catch {
    // no window/search (SSR or a hostile environment) → an empty selection, which is honest
  }
  return { mode, params, cartCount: readCartCount() };
}

/**
 * The destination for switching to `targetHref`'s mountain, carrying the intent.
 *
 * The MODE rule is untouched — this wraps the existing hudNavHref/mountainHubHref resolution rather than
 * restating it, so RW3-3 / F89 R1 cannot drift. What is added is the query: the visitor's already-chosen
 * dates and passengers ride along instead of being dropped on the floor.
 */
export function destinationFor(targetHref: string, intent: SwitchIntent): string {
  const code = mountainCodeFromPath(targetHref);
  const base =
    (intent.mode && code ? hudNavHref(code, intent.mode) : null) ??
    (code ? mountainHubHref(code) : targetHref);
  const keys = Object.keys(intent.params);
  if (!keys.length) return base;
  const q = new URLSearchParams();
  // stable key order so the same intent always produces the same string — the gate compares these
  for (const k of CARRIED_PARAMS) if (intent.params[k] !== undefined) q.set(k, intent.params[k]);
  return `${base}${base.includes("?") ? "&" : "?"}${q.toString()}`;
}

/**
 * A stable, comparable fingerprint of an intent.
 *
 * The gate switches mountains through the dropdown and through the map and asserts THIS string is
 * identical either way. A fingerprint rather than a deep-equal because it is also what gets written to
 * a DOM attribute, so the assertion reads the same value a human can see in the inspector.
 */
export function intentFingerprint(intent: SwitchIntent): string {
  const parts = CARRIED_PARAMS.filter((k) => intent.params[k] !== undefined).map((k) => `${k}=${intent.params[k]}`);
  return `mode:${intent.mode ?? "neutral"}|${parts.join("&")}|cart:${intent.cartCount}`;
}
